import config from "react-global-configuration";
import { getPayload } from "common/services/jwtToken.service";
import { store } from "common/store";
import { Nullable } from "common/types";
import { ILoginData, loadByAltId } from "common/api/user.service";
import { AuthAsyncActions } from "common/reducers/auth.async.actions";
import { AuthActions } from "common/reducers/auth.actions";
import OktaAuth, { TokenResponse, UserClaims } from "@okta/okta-auth-js";
import { createAuth0Client, Auth0Client, User as OidcUser } from "@auth0/auth0-spa-js";
import { trackNrEvent, noticeError } from "common/helpers/NewRelic";

export type TokenType = "occ" | "dabella";
export interface IDabellaRedirectResponse extends ILoginData {
  redirectTo: string;
}

/**
 * The Auth0 doesn't actually expose the refresh token for us to use.  We have a fair bit of
 * code that assumes we have a refreshToken.  So, until we drop everything in favor or Auth0
 * we have this placeholder token.  Easy to recognize as fake when viewing in a browser, but
 * keeps all the legacy code happy.
 */
export const auth0RefreshTokenPlaceholder = "auth0 refresh token";

const dabellaRedirectToKey = "dabellaRedirectTo";

class AuthService implements IAuthService {
  private _userManager: Promise<Auth0Client>;
  private _dabellaUserManager: OktaAuth;
  private _auth0Updater: ReturnType<typeof setTimeout> | null = null;

  public get userManager() {
    return this._userManager;
  }

  public get dabellaUserManager() {
    return this._dabellaUserManager;
  }

  constructor() {
    this._userManager = createAuth0Client({
      domain: config.get("OIDC_DOMAIN"),
      clientId: config.get("OIDC_CLIENT_ID"),
      useRefreshTokens: true,
      authorizationParams: {
        redirect_uri: config.get("OIDC_REDIRECT_URL"),
        audience: config.get("OIDC_AUDIENCE"),
        scope: config.get("OIDC_SCOPE"),
      },
      cacheLocation: "localstorage",
    });
    (window as any).currentUserManager = this._userManager;

    this._dabellaUserManager = new OktaAuth({
      issuer: `https://${config.get("DABELLA_OKTA_DOMAIN") || "dabella.okta.com"}`,
      clientId: config.get("DABELLA_OKTA_CLIENT_ID") || "",
      redirectUri: config.get("DABELLA_OKTA_REDIRECT_URL") || "",
      postLogoutRedirectUri: config.get("DABELLA_OKTA_LOGOUT_URL"),
      scopes: ["email", "profile", "openid", "offline_access", "groups", "address", "phone"],
      pkce: true,
      tokenManager: {
        expireEarlySeconds: 120,
        storage: "localStorage",
        autoRenew: true,
      },
      responseType: ["id_token", "token", "refresh_token"],
    });
    (window as any).currentDabellaUserManager = this._dabellaUserManager;
  }

  public hasToken(): boolean {
    const user = localStorage.getItem("ngStorage-currentUser");
    if (!user || user === "null" || user === "undefined") {
      return false;
    }

    // We could potentially recover from a refresh without a token
    // but we can't recover from a token without a refresh
    if (!this.hasRefreshToken()) {
      return false;
    }

    try {
      const currentUser = JSON.parse(user);
      const isNotEmpty = Object.keys(currentUser.token).length > 0;
      return isNotEmpty;
    } catch (e) {
      return false;
    }
  }

  public getToken() {
    if (!this.hasToken()) {
      return "";
    }

    const user = localStorage.getItem("ngStorage-currentUser");

    const currentUser = JSON.parse(user as string);
    return currentUser.token;
  }

  public getCurrentUser(): { id: number; email: string; token: string } {
    const user = localStorage.getItem("ngStorage-currentUser");
    if (user === null || user === "null") {
      return {} as any;
    }

    return JSON.parse(user);
  }

  public setToken(token: string): void {
    if (!this.hasToken()) {
      throw new Error("Cannot set token without a current user");
    }

    const currentUser = this.getCurrentUser();
    currentUser.token = token;

    localStorage.setItem("ngStorage-currentUser", JSON.stringify(currentUser));
  }

  public setCurrentUser(id: number, email: string, token: string): void {
    const currentUser = {
      id: id,
      email: email,
      token: token,
    };

    localStorage.setItem("ngStorage-currentUser", JSON.stringify(currentUser));
  }

  public hasRefreshToken() {
    const refresh = localStorage.getItem("ngStorage-refresh");
    if (!refresh || refresh === "null" || refresh === "undefined") {
      return "";
    }

    try {
      const refreshObj = JSON.parse(refresh);
      const isNotEmpty = Object.keys(refreshObj.token).length > 0;
      return isNotEmpty;
    } catch (e) {
      return false;
    }
  }

  public getRefreshToken() {
    if (!this.hasRefreshToken()) {
      return "";
    }

    const refresh = localStorage.getItem("ngStorage-refresh");
    const refreshObj = JSON.parse(refresh as string);
    return refreshObj.token;
  }

  public setRefreshToken(token: string): void {
    const refresh = {
      token: token,
    };

    localStorage.setItem("ngStorage-refresh", JSON.stringify(refresh));
  }

  public hasAccessUid() {
    const rsfAuth = sessionStorage.getItem("rsf-auth");
    if (!rsfAuth || rsfAuth === "null" || rsfAuth === "undefined") {
      return "";
    }

    try {
      const rsfAuthObj = JSON.parse(rsfAuth);
      const isNotEmpty = Object.keys(rsfAuthObj.accessUid).length > 0;
      return isNotEmpty;
    } catch (e) {
      return false;
    }
  }

  public getAccessUid() {
    if (!this.hasAccessUid()) {
      return "";
    }

    const rsfAuth = sessionStorage.getItem("rsf-auth");
    const rsfAuthObj = JSON.parse(rsfAuth as string);
    return rsfAuthObj.accessUid;
  }

  public setAccessUid(accessUid: Nullable<string>): void {
    let rsfAuthJson = sessionStorage.getItem("rsf-auth");
    if (!rsfAuthJson || rsfAuthJson === "null" || rsfAuthJson === "undefined") {
      rsfAuthJson = "{}";
    }

    const rsfAuth = JSON.parse(rsfAuthJson);

    rsfAuth.accessUid = accessUid;

    sessionStorage.setItem("rsf-auth", JSON.stringify(rsfAuth));
  }

  public getIdp(): TokenType {
    let rsfAuthJson = localStorage.getItem("rsf-auth");
    if (!rsfAuthJson || rsfAuthJson === "" || rsfAuthJson === "null" || rsfAuthJson === "undefined") {
      rsfAuthJson = "{}";
    }

    let rsfAuth;
    try {
      rsfAuth = JSON.parse(rsfAuthJson);
    } catch (_) {
      rsfAuth = {};
    }

    return rsfAuth.idp || "occ";
  }

  public setIdentityProvider(idp: TokenType): void {
    let rsfAuthJson = localStorage.getItem("rsf-auth");
    if (!rsfAuthJson || rsfAuthJson === "null" || rsfAuthJson === "undefined") {
      rsfAuthJson = "{}";
    }

    const rsfAuth = JSON.parse(rsfAuthJson);

    rsfAuth.idp = idp;

    localStorage.setItem("rsf-auth", JSON.stringify(rsfAuth));
  }

  public clearRsfAuth(): void {
    sessionStorage.setItem("rsf-auth", "null");
  }

  public clearRefreshToken(): void {
    localStorage.setItem("ngStorage-refresh", "null");
  }

  public clearUser(): void {
    localStorage.setItem("ngStorage-currentUser", "null");
  }

  public async auth0Login(redirectTo?: string): Promise<boolean> {
    if (!this.hasToken()) {
      (await this._userManager).loginWithRedirect({ appState: { redirectTo } });
      return false;
    } else {
      try {
        const userManager = await this._userManager;
        const token = await userManager.getTokenSilently();

        this.setRefreshToken(auth0RefreshTokenPlaceholder);
        this.setToken(token);
        store.dispatch(AuthActions.setTokens({ token: token, refreshToken: auth0RefreshTokenPlaceholder }));
      } catch (e) {
        console.error("SilentLogin Attempt: ", e);
      }

      return true;
    }
  }

  public async dabellaLogin(redirectTo?: string): Promise<boolean> {
    this.setIdentityProvider("dabella");
    this._dabellaUserManager.tokenManager.clear();
    // Okta owns the `state` param as its CSRF nonce, so the destination rides the session instead.
    if (redirectTo) {
      sessionStorage.setItem(dabellaRedirectToKey, redirectTo);
    } else {
      sessionStorage.removeItem(dabellaRedirectToKey);
    }
    this._dabellaUserManager.token.getWithRedirect({
      responseType: "code",
      scopes: ["offline_access", "openid", "profile", "email"],
    });
    return false;
  }

  public async handleDabellaRedirect(): Promise<IDabellaRedirectResponse | undefined> {
    let res: TokenResponse;
    try {
      res = await this.dabellaUserManager.token.parseFromUrl();
    } catch (e) {
      console.error("Handle Okta Redirect Error: ", e);
      return;
    }

    if (!res) {
      console.error("No Okta Response: ", res);
      return;
    }
    this.setIdentityProvider("dabella");

    const redirectTo = sessionStorage.getItem(dabellaRedirectToKey) || "";
    sessionStorage.removeItem(dabellaRedirectToKey);

    if (res.tokens.idToken && res.tokens.accessToken && res.tokens.refreshToken) {
      this.dabellaUserManager.tokenManager.add("idToken", res.tokens.idToken);
      this.dabellaUserManager.tokenManager.add("accessToken", res.tokens.accessToken);
      this.dabellaUserManager.tokenManager.add("refreshToken", res.tokens.refreshToken);
    }

    store.dispatch(
      AuthActions.setTokens({
        token: res.tokens.accessToken?.accessToken,
        refreshToken: res.tokens.idToken?.idToken,
      }),
    );
    const userInfo: UserClaims = await this.dabellaUserManager.token.getUserInfo(
      res.tokens.accessToken,
      res.tokens.idToken,
    );

    this.dabellaUserManager.tokenManager.on("renewed", (key: any, newToken: any, oldToken: any) => {
      if (key === "accessToken") {
        store.dispatch(AuthActions.setToken(newToken.accessToken));
      }
    });

    this.dabellaUserManager.start();

    let id: string = userInfo.uid as any;
    if (!id) {
      id = userInfo.sub;
    }
    const rsfUser = await loadByAltId("dabella_id", id, res.tokens.idToken?.idToken);

    return {
      user: rsfUser,
      token: res.tokens.accessToken?.accessToken || "",
      refresh_token: res.tokens.idToken?.idToken || "",
      redirectTo,
    };
  }

  public applyOccToken(newToken: string): void {
    this.setToken(newToken);
    this.setRefreshToken(auth0RefreshTokenPlaceholder);
    store.dispatch(AuthActions.setTokens({ token: newToken, refreshToken: auth0RefreshTokenPlaceholder }));
    this.setupOccManualTokenRefresh();
  }

  public cancelTokenRefresh(): void {
    if (this._auth0Updater) {
      clearTimeout(this._auth0Updater);
      this._auth0Updater = null;
    }
  }

  public setupOccManualTokenRefresh(): void {
    this.cancelTokenRefresh();

    const token = this.getToken();
    let refreshInMs = 60 * 60 * 1000;

    if (token) {
      const payload = getPayload(token);
      if (payload.exp) {
        const expiresAt = payload.exp * 1000;
        const now = Date.now();
        refreshInMs = Math.max(expiresAt - now, 0);
        const bufferMs = Math.min(Math.floor(refreshInMs * 0.05), 60 * 1000);
        refreshInMs = Math.max(refreshInMs - bufferMs, 0);
      }
    }

    this._auth0Updater = setTimeout(async () => {
      try {
        const userManager = await this._userManager;
        const newToken = await userManager.getTokenSilently();
        this.applyOccToken(newToken);
      } catch (e) {
        // setToken throws if the user logged out while getTokenSilently was in-flight — don't reschedule
        noticeError(e, { source: "proactive" });
        trackNrEvent("AuthTokenRefreshFailed", {
          source: "proactive",
          attempt: 1,
          errorMessage: e instanceof Error ? e.message : String(e),
          errorClass: e instanceof Error ? e.constructor.name : undefined,
        });
        console.error("Auth0 proactive token refresh failed", e);
      }
    }, refreshInMs);
  }

  public async forceRefresh(refreshToken: string): Promise<void> {
    await store.dispatch(AuthAsyncActions.refreshToken(refreshToken));
  }

  public async logout(refreshToken: string): Promise<void> {
    this.cancelTokenRefresh();

    authService.clearRefreshToken();
    authService.clearRsfAuth();
    authService.clearUser();

    switch (this.tokenType(refreshToken)) {
      case "dabella":
        await this._dabellaUserManager.signOut();
        this._dabellaUserManager.stop();
        this._dabellaUserManager.tokenManager.clear();
        return;
      case "occ":
      default:
        const userManager = await this._userManager;
        await userManager.logout();
        return;
    }
  }

  public tokenType(token: string): TokenType {
    /**
     * If it's the Auth0 placeholder token, it's not a JWT, we can return fast.
     */
    if (token === auth0RefreshTokenPlaceholder) {
      return "occ";
    }

    const payload = getPayload(token);

    if (payload.iss && payload.iss.indexOf("auth0") >= 0) {
      return "occ";
    }
    if (payload.iss && payload.iss.indexOf("dabella") >= 0) {
      return "dabella";
    }
    return "occ";
  }
}

export interface IAuthService {
  hasToken(): boolean;
  getToken(): string;

  hasRefreshToken(): boolean | "";
  getRefreshToken(): string;
  setRefreshToken(token: string): void;

  hasAccessUid(): boolean | "";
  getAccessUid(): string;
  setAccessUid(accessUid: string): void;

  setCurrentUser(id: number, email: string, token: string): void;
  getCurrentUser(): { id: number; email: string; token: string };

  applyOccToken(newToken: string): void;
  cancelTokenRefresh(): void;
  setupOccManualTokenRefresh(): void;
  forceRefresh(refreshToken: string): Promise<void>;
  logout(refreshToken: string): Promise<void>;
}

export const authService = new AuthService();

export const authErrorMessage = (e: any) => {
  return "There was an unknown error logging into the system. Please contact support if this problem persists.";
};
